/*
* Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
*/
import React, {
  ChangeEvent,
  CSSProperties,
  useEffect,
  useState
} from 'react';
import Tabs from '@material-ui/core/Tabs';
import {GenericObject} from '@ion/core/src/types/entities';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {useExtendStyles} from '../../hooks/useExtendStyles';
import {DLSTheme} from '../../types/types';
import Tab from './Tab';
import styles from './TabView.module.scss';

interface StyleProps {
  backgroundColor?: string;
};

export interface TabPropTypes extends StyleProps {
  tabItems: {tabHeader: string, path?: string, tag?: string}[],
  onChange: (event: ChangeEvent<{}>, value: any) => void,
  onClose?: (event: ChangeEvent<{}>, value: any) => void,
  value?: number,
  reInitialize?: boolean,
  customStyles?: boolean,
  minWidth?: number,
  maxWidth?: number,
  centered?: boolean,
  view?: __esri.MapView;
  style?: CSSProperties;
  overrides?: (theme: DLSTheme) => GenericObject;
  variant?: 'scrollable' | 'fullWidth' | 'standard';
  customTabs?: boolean;
  isCondensedVariant?: boolean;
  closeForFirstTab?: boolean;
};

const useStyles = () => ({
  customTabIndicator : {
    backgroundColor : dlsColor('black')
  },
  tabRoot : {
  },
  tabContainer : {
  },
  endAdornment : {
    position : 'absolute',
    right    : 0
  }
});

/**
 * @TabView
 *
 * Defines the Tabs component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 **
 *  <TabView tabItems={tabs} onChange={handleChange} value={value} />
 *
 * ```
 *
 */
export const TabView = ({
  tabItems,
  onChange,
  onClose,
  reInitialize,
  customStyles,
  minWidth,
  maxWidth,
  value,
  view,
  overrides,
  style,
  centered = true,
  customTabs = false,
  closeForFirstTab = false,
  isCondensedVariant = false,
  variant = 'fullWidth'
}: TabPropTypes) => {
  const tabViewStyles                       = useExtendStyles(useStyles, overrides);
  const classes                             = tabViewStyles();
  const [initializeTabs, setInitializeTabs] = useState(false);

  useEffect(() => {
    if (view) {
      const listener = view?.popup.watch('visible', function() {
        if (view?.popup.visible) {
          setInitializeTabs(true);
        }
        return function() {
          listener?.remove();
        };
      });
    } else {
      setInitializeTabs(true);
    }
  }, []);

  useEffect(() => {
    setInitializeTabs(true);
  }, [reInitialize]);

  return (
    <div className={customStyles ? styles.tabContainerAlt : styles.tabContainer} style={style}>
      {initializeTabs && (
        <Tabs value={value}
          onChange={onChange}
          indicatorColor='secondary'
          textColor='secondary'
          classes={{
            indicator     : classes.customTabIndicator,
            flexContainer : classes.tabContainer,
            root          : classes.tabRoot,
            scroller      : classes?.scroller
          }}
          variant={variant}
          centered={centered}>
          {tabItems?.map((tab, index) => {
            const condition = ((index !== 0 || closeForFirstTab) && customTabs);
            return (
              <Tab index={index}
                tab={tab}
                onClick={onChange}
                showToolTip={condition}
                showIcon={condition}
                maxWidth={maxWidth}
                minWidth={minWidth}
                customTabs={customTabs}
                customStyles={customStyles}
                isCondensedVariant={isCondensedVariant}
                overrides={overrides}
                onClose={onClose} />
            );
          })}
        </Tabs>
      )}
    </div>
  );
};

export default TabView;
